import { useState } from "react";
import { siteConfig } from "../../config/site";
import { useInView } from "../../hooks/useInView";
import zapIcon from "../../assets/home-page/loan-application-form/zap-icon.svg";
import shieldCheckIcon from "../../assets/home-page/loan-application-form/shield-check-icon.svg";
import documentListIcon from "../../assets/home-page/loan-application-form/document-list-icon.svg";
import submitArrowIcon from "../../assets/home-page/loan-application-form/submit-arrow-icon.svg";
import lockIcon from "../../assets/home-page/loan-application-form/lock-icon.svg";

const FEATURES = [
  {
    icon: zapIcon,
    title: "Quick & easy application",
    description: "Apply in just a few minutes",
  },
  {
    icon: shieldCheckIcon,
    title: "Safe & secure",
    description: "Your data is always protected",
  },
  {
    icon: documentListIcon,
    title: "Personalized offers",
    description: "Get options that fit your needs",
  },
];

function FormField({
  label,
  placeholder,
  uppercasePlaceholder,
}: {
  label: string;
  placeholder: string;
  uppercasePlaceholder?: boolean;
}) {
  return (
    <label className="flex flex-col gap-[6px]">
      <span className="text-sm font-semibold leading-5 text-[#370b56]">
        {label}
      </span>
      <input
        type="text"
        placeholder={placeholder}
        className={`rounded-xl border border-solid border-[#f3e8ff] bg-white px-[17px] py-[14px] text-sm text-[#1c1917] shadow-[0px_1px_2px_rgba(0,0,0,0.02)] outline-none placeholder:text-[#94a3b8] focus:border-[#370b56] ${
          uppercasePlaceholder ? "placeholder:uppercase" : ""
        }`}
      />
    </label>
  );
}

export function LoanApplicationForm() {
  const [employmentType, setEmploymentType] = useState<"salaried" | "self">(
    "salaried"
  );
  const { ref: headerRef, inView: headerInView } = useInView<HTMLDivElement>();

  return (
    <section className="mx-auto w-full max-w-[1280px] px-4 pb-16 pt-8 sm:pb-24 sm:pt-10">
      <div className="rounded-[32px] border border-solid border-[rgba(243,232,255,0.7)] bg-[#f7f2fb] p-6 shadow-[0px_1px_1px_rgba(0,0,0,0.05)] sm:p-[49px]">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Left column */}
          <div className="flex flex-col justify-between lg:col-span-5 lg:pr-8">
            <div
              ref={headerRef}
              className={`flex flex-col gap-3 ${headerInView ? "in-view" : ""}`}
            >
              <span className="reveal-scroll delay-1 text-xs font-bold uppercase tracking-[1.2px] text-[#0e8374]">
                Apply in minutes
              </span>
              <h2 className="reveal-scroll delay-2 text-3xl font-semibold leading-tight tracking-[-0.9px] text-[#370b56] sm:text-4xl">
                Fill out the form
              </h2>
              <p className="reveal-scroll delay-3 pt-1 text-base leading-6 text-[#475569]">
                Share a few details to check your loan options. It's quick,
                simple and secure.
              </p>
            </div>

            <ul className="mt-8 flex flex-col gap-6">
              {FEATURES.map((feature) => (
                <li key={feature.title} className="flex items-start gap-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#daf4ed]">
                    <img src={feature.icon} alt="" className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="text-sm font-bold leading-5 text-[#370b56]">
                      {feature.title}
                    </p>
                    <p className="text-sm leading-5 text-[#64748b]">
                      {feature.description}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Divider (desktop only) */}
          <div className="hidden lg:col-span-1 lg:block">
            <div className="mx-auto h-full w-px bg-[rgba(233,213,255,0.5)]" />
          </div>

          {/* Right column: form */}
          <div className="flex flex-col gap-6 lg:col-span-6 lg:pl-4">
            <div>
              <span className="text-sm font-semibold leading-5 text-[#370b56]">
                Employment Type
              </span>
              <div className="mt-2 flex w-full max-w-[320px] items-center gap-1 rounded-full border border-solid border-[rgba(233,213,255,0.8)] bg-white/70 p-[5px] backdrop-blur-[4px]">
                {(["salaried", "self"] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setEmploymentType(type)}
                    className={`flex-1 rounded-full py-2 text-sm font-medium transition-colors ${
                      employmentType === type
                        ? "bg-[#370b56] text-white"
                        : "text-[#370b56]"
                    }`}
                  >
                    {type === "salaried" ? "Salaried" : "Self Employed"}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField label="Full Name" placeholder="Enter your full name" />
              <FormField
                label="PAN Number"
                placeholder="Enter your PAN number"
                uppercasePlaceholder
              />
              <FormField
                label="Mobile Number"
                placeholder="Enter 10 digit mobile number"
              />
              <FormField
                label="Monthly Salary"
                placeholder="Enter your monthly salary"
              />
            </div>

            <button
              type="button"
              onClick={() => {
                if (siteConfig.applyLink) {
                  window.open(siteConfig.applyLink, "_blank", "noopener,noreferrer");
                }
              }}
              className="flex items-center justify-center gap-3 rounded-full bg-[#370b56] px-6 py-[14px] text-base font-semibold text-white shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.1),0px_2px_4px_-2px_rgba(0,0,0,0.1)] transition-opacity hover:opacity-90"
            >
              Apply Now
              <img src={submitArrowIcon} alt="" className="h-4 w-4" />
            </button>

            <div className="flex items-center justify-center gap-2">
              <img src={lockIcon} alt="" className="h-[14px] w-[14px]" />
              <span className="text-xs leading-4 text-[#64748b]">
                Your information is protected and used only to check loan
                options.
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
